import { useState, useEffect, useCallback } from 'react';
import { pwaLogger } from '../utils/logger';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export interface PWAState {
  isInstallable: boolean;
  isInstalled: boolean;
  isSupported: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  hasPrompted: boolean;
  installPrompt: BeforeInstallPromptEvent | null;
  showInstallPrompt: boolean;
  interactionCount: number;
}

export const usePWA = () => {
  // Note: onboarding completion is handled separately in components that have Router context
  const [pwaState, setPwaState] = useState<PWAState>({
    isInstallable: false,
    isInstalled: false,
    isSupported: false,
    isIOS: false,
    isAndroid: false,
    hasPrompted: false,
    installPrompt: null,
    showInstallPrompt: false,
    interactionCount: 0
  });

  // Detect device and browser capabilities
  const detectEnvironment = useCallback(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    const isSupported = 'serviceWorker' in navigator;
    
    // Enhanced PWA installation detection
    const isInstalled = 
      // Standard PWA detection
      window.matchMedia('(display-mode: standalone)').matches ||
      // iOS Safari detection
      (window.navigator as any).standalone === true ||
      // Alternative detection for some browsers
      window.matchMedia('(display-mode: minimal-ui)').matches ||
      // Check if launched from home screen
      document.referrer.includes('android-app://');

    return { isIOS, isAndroid, isSupported, isInstalled };
  }, []);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        pwaLogger.register('Service Worker registered', registration);
        
        // Handle updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                pwaLogger.update('New version available');
                // Could show update notification here
              }
            });
          }
        });
        
        return registration;
      } catch (error) {
        pwaLogger.error('Service Worker registration failed', error);
        return null;
      }
    }
    return null;
  }, []);

  // Track user interactions
  const incrementInteraction = useCallback(() => {
    const currentCount = parseInt(localStorage.getItem('pwa_interactions') || '0') + 1;
    localStorage.setItem('pwa_interactions', currentCount.toString());
    
    setPwaState(prev => ({
      ...prev,
      interactionCount: currentCount
    }));
  }, []);

  // Check if should show install prompt
  const shouldShowPrompt = useCallback((interactionCount: number, hasPrompted: boolean, isInstallable: boolean) => {
    const hasBeenDismissed = localStorage.getItem('pwa_dismissed') === 'true';
    const minInteractions = 3; // Show after 3 interactions
    
    return (
      isInstallable &&
      !hasPrompted &&
      !hasBeenDismissed &&
      interactionCount >= minInteractions
    );
  }, []);

  // Install the PWA
  const installPWA = useCallback(async () => {
    if (!pwaState.installPrompt) {
      pwaLogger.error('No install prompt available');
      return false;
    }

    try {
      await pwaState.installPrompt.prompt();
      const choiceResult = await pwaState.installPrompt.userChoice;
      
      pwaLogger.install('User choice:', choiceResult.outcome);
      
      if (choiceResult.outcome === 'accepted') {
        localStorage.setItem('pwa_installed', 'true');
        setPwaState(prev => ({
          ...prev,
          isInstalled: true,
          showInstallPrompt: false,
          hasPrompted: true
        }));
        
        // Note: onboarding completion handled in PWAInstallPrompt component
        
        return true;
      } else {
        dismissPrompt();
        return false;
      }
    } catch (error) {
      pwaLogger.error('Installation failed', error);
      return false;
    }
  }, [pwaState.installPrompt]);

  // Dismiss install prompt
  const dismissPrompt = useCallback(() => {
    localStorage.setItem('pwa_dismissed', 'true');
    setPwaState(prev => ({
      ...prev,
      showInstallPrompt: false,
      hasPrompted: true
    }));
  }, []);

  // Reset PWA state (for testing/debugging)
  const resetPWAState = useCallback(() => {
    localStorage.removeItem('pwa_dismissed');
    localStorage.removeItem('pwa_installed');
    localStorage.removeItem('pwa_interactions');
    
    setPwaState(prev => ({
      ...prev,
      hasPrompted: false,
      showInstallPrompt: false,
      interactionCount: 0
    }));
  }, []);

  // Get installation instructions for iOS
  const getIOSInstructions = useCallback(() => {
    return [
      'Toca el botón de compartir en Safari',
      'Selecciona "Añadir a pantalla de inicio"',
      'Confirma el nombre y toca "Añadir"'
    ];
  }, []);

  // Open PWA as standalone app
  const openAsApp = useCallback(() => {
    try {
      // Try to open with window.open to launch PWA
      const appUrl = window.location.origin;
      const appWindow = window.open(appUrl, '_blank', 'toolbar=no,menubar=no,resizable=yes,scrollbars=yes');
      
      if (appWindow) {
        // Focus the new window/app
        appWindow.focus();
        return true;
      }
      
      // Fallback: navigate to home in same window
      window.location.href = appUrl;
      return true;
    } catch (error) {
      pwaLogger.error('Failed to open as app', error);
      return false;
    }
  }, []);

  // Check if notifications are supported and enabled
  const getNotificationStatus = useCallback(() => {
    if (!('Notification' in window)) {
      return { supported: false, permission: 'not-supported' };
    }
    
    return {
      supported: true,
      permission: Notification.permission,
      pushSupported: 'PushManager' in window && 'serviceWorker' in navigator
    };
  }, []);

  useEffect(() => {
    const environment = detectEnvironment();
    const savedInteractions = parseInt(localStorage.getItem('pwa_interactions') || '0');
    const hasBeenDismissed = localStorage.getItem('pwa_dismissed') === 'true';
    
    // Note: onboarding step completion handled in components with Router context
    
    setPwaState(prev => ({
      ...prev,
      ...environment,
      interactionCount: savedInteractions,
      hasPrompted: hasBeenDismissed
    }));

    // Register service worker
    registerServiceWorker();

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const event = e as BeforeInstallPromptEvent;
      
      pwaLogger.register('Before install prompt fired');
      
      setPwaState(prev => {
        const newState = {
          ...prev,
          isInstallable: true,
          installPrompt: event,
          showInstallPrompt: shouldShowPrompt(prev.interactionCount, prev.hasPrompted, true)
        };
        return newState;
      });
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      pwaLogger.success('App was installed');
      localStorage.setItem('pwa_installed', 'true');
      
      setPwaState(prev => ({
        ...prev,
        isInstalled: true,
        showInstallPrompt: false,
        hasPrompted: true
      }));
      
      // Note: onboarding completion handled in PWAInstallPrompt component
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [detectEnvironment, registerServiceWorker, shouldShowPrompt]);

  // Check if should show prompt when interaction count changes
  useEffect(() => {
    if (pwaState.isInstallable && !pwaState.hasPrompted) {
      const shouldShow = shouldShowPrompt(
        pwaState.interactionCount, 
        pwaState.hasPrompted, 
        pwaState.isInstallable
      );
      
      if (shouldShow !== pwaState.showInstallPrompt) {
        setPwaState(prev => ({
          ...prev,
          showInstallPrompt: shouldShow
        }));
      }
    }
  }, [pwaState.interactionCount, pwaState.isInstallable, pwaState.hasPrompted, shouldShowPrompt]);

  return {
    ...pwaState,
    installPWA,
    dismissPrompt,
    incrementInteraction,
    resetPWAState,
    getIOSInstructions,
    openAsApp,
    getNotificationStatus
  };
};